import { defineNuxtPlugin } from '#app'

/**
 * Origem Flow: fallback de composição de acentos (teclas mortas).
 *
 * Janelas de app instalado do Chrome (PWA) têm um bug do Chromium em que,
 * após suspensão do sistema ou troca de layout, o IME da janela para de
 * compor teclas mortas: ´ + a deixa de produzir á (o mesmo site continua
 * normal numa aba comum). O estado do IME vive no processo do navegador,
 * então a página não consegue restaurá-lo — mas consegue detectar a falha
 * e compor os acentos por conta própria.
 *
 * Detecção: num IME saudável, o keydown da tecla morta (key === 'Dead') é
 * seguido imediatamente por compositionstart, e o keydown seguinte chega
 * com isComposing = true. Quando a composição está quebrada, nada disso
 * acontece — só então este fallback age, inserindo o caractere acentuado
 * manualmente. Com IME saudável ele nunca interfere.
 *
 * Exceção (Windows): o Chrome no Windows compõe teclas mortas no próprio
 * SO, sem nenhum evento de composição — o keydown seguinte já chega com o
 * caractere composto (key === 'ã', não 'a'). Isso não é composição
 * quebrada; ao detectar uma tecla já composta, o fallback se desativa de
 * vez nesta janela.
 */

// Qual acento cada tecla morta representa, por layout, indexado por
// event.code e shiftKey. ABNT2 é o padrão (usuários brasileiros);
// US-Internacional é detectado via Keyboard Layout API quando disponível.
const DEAD_KEYS = {
  abnt2: {
    BracketLeft: { unshifted: 'acute', shifted: 'grave' },
    Quote: { unshifted: 'tilde', shifted: 'circumflex' },
    Digit6: { shifted: 'diaeresis' },
  },
  'us-intl': {
    Quote: { unshifted: 'acute', shifted: 'diaeresis' },
    Backquote: { unshifted: 'grave', shifted: 'tilde' },
    Digit6: { shifted: 'circumflex' },
  },
}

const ACCENT_CHAR = {
  acute: '´',
  grave: '`',
  tilde: '~',
  circumflex: '^',
  diaeresis: '¨',
}

const COMPOSE = {
  acute: {
    a: 'á', e: 'é', i: 'í', o: 'ó', u: 'ú', c: 'ç', y: 'ý',
    A: 'Á', E: 'É', I: 'Í', O: 'Ó', U: 'Ú', C: 'Ç', Y: 'Ý',
  },
  grave: {
    a: 'à', e: 'è', i: 'ì', o: 'ò', u: 'ù',
    A: 'À', E: 'È', I: 'Ì', O: 'Ò', U: 'Ù',
  },
  tilde: {
    a: 'ã', o: 'õ', n: 'ñ',
    A: 'Ã', O: 'Õ', N: 'Ñ',
  },
  circumflex: {
    a: 'â', e: 'ê', i: 'î', o: 'ô', u: 'û',
    A: 'Â', E: 'Ê', I: 'Î', O: 'Ô', U: 'Û',
  },
  diaeresis: {
    a: 'ä', e: 'ë', i: 'ï', o: 'ö', u: 'ü',
    A: 'Ä', E: 'Ë', I: 'Ï', O: 'Ö', U: 'Ü',
  },
}

const MODIFIER_KEYS = new Set([
  'Shift',
  'CapsLock',
  'Control',
  'Alt',
  'AltGraph',
  'Meta',
])

// Uma tecla morta pendente expira depois disso — o usuário claramente
// desistiu da composição.
const PENDING_TIMEOUT_MS = 5000

function isEditable(el) {
  if (!el) return false
  const tag = el.tagName
  if (tag === 'TEXTAREA') return true
  if (tag === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase()
    return ['text', 'search', 'email', 'url', 'tel', 'password'].includes(type)
  }
  return el.isContentEditable === true
}

function insertText(text) {
  // execCommand dispara os eventos de input que o Vue (v-model) e os
  // editores (ProseMirror etc.) esperam, e preserva o undo nativo.
  document.execCommand('insertText', false, text)
}

export default defineNuxtPlugin(() => {
  if (!import.meta.client) return

  let layout = 'abnt2'
  let layoutDetected = false
  let pending = null
  // Verdadeiro quando o SO compõe teclas mortas sozinho (Windows): o
  // keydown pós-tecla-morta chega com o caractere já composto. Nesse
  // ambiente o fallback nunca deve agir.
  let nativeComposes = false

  function detectLayout() {
    if (layoutDetected) return
    layoutDetected = true
    try {
      if (navigator.keyboard && navigator.keyboard.getLayoutMap) {
        navigator.keyboard.getLayoutMap().then((map) => {
          if (map.get('Semicolon') === 'ç') {
            layout = 'abnt2'
          } else if (map.get('Quote') === "'") {
            layout = 'us-intl'
          }
        })
      }
    } catch (e) {
      // Sem a API, fica no padrão ABNT2.
    }
  }

  function accentForEvent(event) {
    const entry = DEAD_KEYS[layout][event.code]
    if (!entry) return null
    return (event.shiftKey ? entry.shifted : entry.unshifted) || null
  }

  window.addEventListener(
    'compositionstart',
    () => {
      // IME saudável assumiu a composição — nunca interferir.
      pending = null
    },
    true
  )

  window.addEventListener(
    'keydown',
    (event) => {
      if (nativeComposes) return

      // Composição ativa = IME funcionando; este fallback não age.
      if (event.isComposing || event.key === 'Process') {
        pending = null
        return
      }

      if (event.key === 'Dead') {
        detectLayout()
        const previous = pending
        pending = null
        const accent = accentForEvent(event)
        // Tecla morta repetida com composição quebrada (ex.: ´´): a
        // primeira ainda estava pendente quando a segunda chegou, prova
        // de que o compositionstart nunca veio — insere a literal.
        if (previous && isEditable(event.target)) {
          insertText(ACCENT_CHAR[previous.accent])
        }
        if (accent) {
          pending = { accent, time: Date.now() }
        }
        return
      }

      if (pending === null) return
      if (MODIFIER_KEYS.has(event.key)) return
      if (Date.now() - pending.time > PENDING_TIMEOUT_MS) {
        pending = null
        return
      }

      const isPrintable =
        event.key.length === 1 && !event.ctrlKey && !event.metaKey
      if (!isPrintable || !isEditable(event.target)) {
        pending = null
        return
      }

      // Tecla já composta (ex.: 'ã' em vez de 'a'): o SO compôs a tecla
      // morta nativamente, sem eventos de composição — comportamento
      // normal do Chrome no Windows. Pipeline saudável; interferir aqui
      // duplicaria o acento ("ç~ão"). Desativa o fallback de vez.
      if (event.key.charCodeAt(0) > 127) {
        nativeComposes = true
        pending = null
        return
      }

      // Chegou aqui: houve tecla morta, nenhum compositionstart e agora um
      // caractere imprimível — a composição do navegador está quebrada.
      // Compõe manualmente, como o IME faria.
      const { accent } = pending
      pending = null
      event.preventDefault()
      if (event.key === ' ') {
        insertText(ACCENT_CHAR[accent])
      } else {
        const composed = COMPOSE[accent][event.key]
        insertText(composed || ACCENT_CHAR[accent] + event.key)
      }
    },
    true
  )
})
